import React, { useRef, useEffect, useState } from 'react';
import { ChartConfig } from '../../../types';
import { ForceGraphConfig, ForceGraphMapping } from './config/forceGraphTypes';
import { DEFAULT_FORCE_GRAPH_CONFIG } from './config/forceGraphDefaults';
import { useForceGraphData } from './hooks/useForceGraphData';
import { COLOR_PALETTES, DEFAULT_PALETTE } from '../../../constants';
import { ForceGraphCanvas } from './ForceGraphCanvas';
import { ForceGraphLegend } from './ui/ForceGraphLegend';

interface ForceGraphChartProps {
    config: ChartConfig;
    data: any[]; // Raw data from dataService
}

export const ForceGraphChart: React.FC<ForceGraphChartProps> = ({ config, data }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    // Merge config with defaults
    const forceConfig: ForceGraphConfig = {
        ...DEFAULT_FORCE_GRAPH_CONFIG,
        ...(config.advanced?.forceGraph || {}),
        showNodeLabels: config.customization?.showDataLabels ?? DEFAULT_FORCE_GRAPH_CONFIG.showNodeLabels
    };

    // Create Mapping from ChartConfig
    const mapping: ForceGraphMapping = {
        nodeIdField: config.sourceField || 'source',
        linkSourceField: config.sourceField || 'source',
        linkTargetField: config.targetField || 'target',
        nodeLabelField: config.sourceField, // Default label to ID
        nodeGroupField: config.encoding?.color?.field,
        linkValueField: config.valueField,
        linkTypeField: config.encoding?.color?.field, // Re-use color field for link type if applicable
    };

    const palette = (config.customization?.paletteName && config.customization.paletteName !== 'custom')
        ? COLOR_PALETTES[config.customization.paletteName]
        : (config.customization?.customPalette || COLOR_PALETTES[DEFAULT_PALETTE]);

    const graphData = useForceGraphData(data, mapping, palette);

    // Handle Resize
    useEffect(() => {
        if (!containerRef.current) return;
        const resizeObserver = new ResizeObserver(entries => {
            if (entries[0]) {
                const { width, height } = entries[0].contentRect;
                setDimensions({ width, height });
            }
        });
        resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect();
    }, []);

    return (
        <div ref={containerRef} className="w-full h-full relative overflow-hidden bg-black/20 rounded-xl border border-white/5">
            {dimensions.width > 0 && dimensions.height > 0 && (
                <ForceGraphCanvas
                    data={graphData}
                    config={forceConfig}
                    width={dimensions.width}
                    height={dimensions.height}
                />
            )}
            {forceConfig.legendMode !== 'none' && (
                <ForceGraphLegend data={graphData} />
            )}
        </div>
    );
};
